import CloseIcon from '@mui/icons-material/Close';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ProductImage } from '../../../types';

type ImageTarget = 'primary' | 'secondary';
type ConflictResolution = 'delete' | 'demote';

interface UseCropAsProductImageDialogProps {
  isOpen: boolean;
  cropImageUrl: string;
  existingPrimary: ProductImage | null;
  saving?: boolean;
  onCancel: () => void;
  onConfirm: (target: ImageTarget, conflictResolution?: ConflictResolution) => void;
}

async function fetchImageBlob(url: string): Promise<Blob> {
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.status} ${response.statusText} (${url})`);
  }
  return response.blob();
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.36)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 30,
};

const cardStyle: React.CSSProperties = {
  width: 380,
  maxWidth: 'calc(100vw - 32px)',
  maxHeight: 'calc(100vh - 48px)',
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  boxShadow: '0 14px 32px rgba(15, 23, 42, 0.22)',
  padding: 14,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  overflowY: 'auto',
};

export const UseCropAsProductImageDialog: React.FC<UseCropAsProductImageDialogProps> = ({
  isOpen,
  cropImageUrl,
  existingPrimary,
  saving = false,
  onCancel,
  onConfirm,
}) => {
  const [phase, setPhase] = useState<'choose' | 'conflict'>('choose');
  const [target, setTarget] = useState<ImageTarget>('primary');
  const [blobReady, setBlobReady] = useState(false);
  const blobReadyRef = useRef(false);

  // Fetch the crop image blob on open so the parent's upload won't have to wait
  // (and so we can disable Confirm until the image is reachable). We don't pass
  // the blob back — the parent re-fetches via the shared helper — but this both
  // validates reachability and warms the browser cache.
  useEffect(() => {
    if (!isOpen || !cropImageUrl) {
      setBlobReady(false);
      blobReadyRef.current = false;
      return;
    }
    setPhase('choose');
    setTarget('primary');
    setBlobReady(false);
    blobReadyRef.current = false;
    let cancelled = false;
    fetchImageBlob(cropImageUrl)
      .then(() => {
        if (cancelled) return;
        blobReadyRef.current = true;
        setBlobReady(true);
      })
      .catch((err) => {
        console.error('Failed to fetch crop image blob:', err);
        if (cancelled) return;
        blobReadyRef.current = true;
        setBlobReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, cropImageUrl]);

  const handleChooseConfirm = useCallback(() => {
    if (target === 'secondary') {
      onConfirm('secondary');
      return;
    }
    // Primary: if a managed primary already exists, ask how to resolve.
    if (existingPrimary?.productImageId) {
      setPhase('conflict');
      return;
    }
    onConfirm('primary');
  }, [target, existingPrimary, onConfirm]);

  if (!isOpen) return null;

  return (
    <div style={overlayStyle} onClick={onCancel} data-testid="use-crop-as-product-image-overlay">
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
        >
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700 }}>
            {phase === 'choose' ? 'Use crop as product image' : 'Primary image already exists'}
          </div>
          <CloseIcon
            onClick={onCancel}
            style={{ fontSize: 14, cursor: 'pointer', color: 'var(--text-tertiary)' }}
          />
        </div>

        {/* Crop preview */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            background: 'var(--bg)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            padding: 8,
          }}
        >
          <img
            src={cropImageUrl}
            alt="Crop preview"
            style={{ maxHeight: 220, maxWidth: '100%', objectFit: 'contain', display: 'block' }}
          />
        </div>

        {phase === 'choose' ? (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {(['primary', 'secondary'] as const).map((option) => {
                const isSelected = target === option;
                return (
                  <button
                    key={option}
                    onClick={() => setTarget(option)}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      padding: '8px 10px',
                      borderRadius: 'var(--radius-sm)',
                      border: isSelected
                        ? '1px solid var(--accent-border)'
                        : '1px solid var(--border)',
                      background: isSelected ? 'var(--accent-subtle)' : 'var(--bg)',
                      color: 'var(--text-primary)',
                      cursor: 'pointer',
                      fontSize: 11,
                      fontWeight: 600,
                      textAlign: 'left',
                    }}
                  >
                    <span style={{ fontFamily: 'var(--font-mono)' }}>
                      {option === 'primary' ? 'Primary product image' : 'Secondary product image'}
                    </span>
                    <span style={{ color: 'var(--text-tertiary)', fontWeight: 500 }}>
                      {option === 'primary'
                        ? 'The main image shown for this product'
                        : 'Added as an additional reference image'}
                    </span>
                  </button>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={onCancel}
                style={{
                  padding: '6px 12px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)',
                  background: 'var(--bg)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                Cancel
              </button>
              <button
                disabled={!blobReady || saving}
                onClick={handleChooseConfirm}
                style={{
                  padding: '6px 12px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--accent-border)',
                  background: blobReady && !saving ? 'var(--accent)' : 'var(--border)',
                  color: blobReady && !saving ? 'var(--on-accent, #fff)' : 'var(--text-tertiary)',
                  cursor: blobReady && !saving ? 'pointer' : 'not-allowed',
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                {saving ? 'Saving…' : blobReady ? 'Confirm' : 'Loading…'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
              A primary product image already exists. Choose what to do with it before setting this
              crop as the new primary.
            </div>
            {existingPrimary?.imageUri && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ fontSize: 10, color: 'var(--text-tertiary)', width: 56 }}>
                  Current primary
                </div>
                <img
                  src={existingPrimary.imageUri}
                  alt="Current primary"
                  style={{
                    height: 64,
                    width: 64,
                    objectFit: 'contain',
                    background: 'var(--bg)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                  }}
                />
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                disabled={saving}
                onClick={() => onConfirm('primary', 'demote')}
                style={{
                  padding: '6px 12px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)',
                  background: 'var(--bg)',
                  color: 'var(--text-primary)',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                Make existing secondary
              </button>
              <button
                disabled={saving}
                onClick={() => onConfirm('primary', 'delete')}
                style={{
                  padding: '6px 12px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--danger-border, var(--border))',
                  background: 'var(--danger, #dc2626)',
                  color: '#fff',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                Delete existing
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
